import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, BedDouble, Maximize, ArrowUpRight } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

const cardVariants = {
  hidden: { opacity: 0, y: 48 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export const ApartmentGrid = () => {
  const { lang, t } = useLang();
  const [apartments, setApartments] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data.filter((a) => !["Вили", "Хотели"].includes(a.category_mk) && !["Villas", "Hotels"].includes(a.category_en)))).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    apartments.forEach((apt) => {
      const cat = (lang === "mk" ? apt.category_mk : apt.category_en) || "";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(apt);
    });
    return [...map.entries()].sort(([a], [b]) => {
      const rank = (c) => (c.includes("Манго") || c.includes("Mango") ? 0 : c.includes("Нептун") || c.includes("Neptune") ? 1 : 2);
      return rank(a) - rank(b);
    });
  }, [apartments, lang]);

  const tabs = useMemo(() => {
    const mangoCat = groups.find(([c]) => c.includes("Манго") || c.includes("Mango"))?.[0];
    const neptunCat = groups.find(([c]) => c.includes("Нептун") || c.includes("Neptune"))?.[0];
    return [
      { id: "all", label: t("grid.tabAll") },
      { id: "mango", label: mangoCat || "Манго", match: ["Манго", "Mango"] },
      { id: "greenplace", label: t("grid.greenPlace"), match: ["Green Place", "Грин Плејс"] },
      { id: "neptun", label: neptunCat || "Нептун", match: ["Нептун", "Neptune"] },
    ];
  }, [groups, t]);

  const visibleGroups = useMemo(() => {
    if (activeTab === "all") return groups;
    const tab = tabs.find((tb) => tb.id === activeTab);
    if (!tab || !tab.match) return groups;
    return groups.filter(([cat]) => tab.match.some((m) => cat.includes(m)));
  }, [groups, activeTab, tabs]);

  return (
    <section id="apartments" data-testid="apartments-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
      >
        <div>
          <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("grid.kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">{t("grid.title")}</h2>
        </div>
        <p className="text-ink/60 text-sm md:text-base max-w-sm leading-relaxed">{t("grid.subtitle")}</p>
      </motion.div>

      <div className="flex flex-wrap gap-3 mb-12 md:mb-16" data-testid="apartments-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`apartments-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 text-xs font-bold tracking-[0.15em] uppercase border transition-colors duration-300 ${
              activeTab === tab.id ? "bg-terra text-sand border-terra" : "border-ink/25 text-ink/70 hover:border-terra hover:text-terra"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {visibleGroups.length === 0 && (
        <div data-testid="apartments-empty" className="border border-line/70 bg-coal text-sand p-12 md:p-16 text-center">
          <p className="font-serif italic text-4xl md:text-5xl text-ember mb-6">{t("grid.comingSoon")}</p>
          <p className="text-sand/60 text-sm md:text-base max-w-md mx-auto leading-relaxed">{t("rental.sub")}</p>
        </div>
      )}

      {visibleGroups.map(([category, items], gi) => (
        <div key={category || gi} className="mb-20 md:mb-28 last:mb-0">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-baseline gap-5 mb-8 md:mb-12"
            data-testid={`category-heading-${gi}`}
          >
            <span className="font-serif italic text-4xl md:text-5xl text-terra/50 leading-none">{String(gi + 1).padStart(2, "0")}</span>
            <h3 className="font-serif text-2xl md:text-4xl tracking-tight">{category}</h3>
            <span className="flex-1 h-px bg-line/70 self-center" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {items.map((apt, i) => (
              <motion.div
                key={apt.slug}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className={i % 2 === 1 ? "md:mt-20" : ""}
              >
                <Link
                  to={`/apartments/${apt.slug}`}
                  data-testid={`apartment-card-${apt.slug}`}
                  className="group block border border-line/70 bg-sand hover:shadow-[0_24px_60px_-24px_rgba(44,42,38,0.35)] transition-shadow duration-700"
                >
                  {apt.images.length > 0 ? (
                    <div className="spotlight-frame aspect-[4/3]">
                      <img src={apt.images[0]} alt={lang === "mk" ? apt.name_mk : apt.name_en} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-4 left-4 bg-coal/80 backdrop-blur px-3 py-1.5 text-sand text-[11px] font-semibold tracking-widest uppercase">
                        {lang === "mk" ? apt.location_mk : apt.location_en}
                      </div>
                      {apt.coming_soon && (
                        <div className="absolute top-4 right-4 bg-ember px-3 py-1.5 text-coal text-[11px] font-bold tracking-widest uppercase" data-testid={`coming-soon-badge-${apt.slug}`}>
                          {t("grid.comingSoon")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-coal flex items-center justify-center">
                      <span className="font-serif italic text-3xl md:text-4xl text-ember" data-testid={`coming-soon-badge-${apt.slug}`}>{t("grid.comingSoon")}</span>
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-2xl md:text-3xl tracking-tight group-hover:text-terra transition-colors duration-300">
                          {lang === "mk" ? apt.name_mk : apt.name_en}
                        </h3>
                        <p className="text-ink/55 text-sm mt-1">{lang === "mk" ? apt.tagline_mk : apt.tagline_en}</p>
                      </div>
                      <div className="w-10 h-10 shrink-0 border border-line flex items-center justify-center group-hover:bg-terra group-hover:border-terra group-hover:text-sand transition-colors duration-300">
                        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="mt-6 pt-5 border-t border-line/70 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-5 text-ink/60">
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="w-4 h-4" strokeWidth={1.5} /> {apt.max_guests} {t("grid.guests")}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <BedDouble className="w-4 h-4" strokeWidth={1.5} /> {apt.bedrooms === 0 ? t("grid.studio") : `${apt.bedrooms} ${t("grid.bedrooms")}`}
                        </span>
                        <span className="hidden sm:inline-flex items-center gap-1.5">
                          <Maximize className="w-4 h-4" strokeWidth={1.5} /> {apt.size_m2} m²
                        </span>
                      </div>
                      {apt.coming_soon ? (
                        <p className="font-serif text-xl italic text-terra whitespace-nowrap">{t("grid.comingSoon")}</p>
                      ) : (
                        <p className="font-serif text-xl whitespace-nowrap">
                          <span className="text-xs font-sans text-ink/50 mr-1">{t("grid.from")}</span>€{apt.price_per_night}
                          <span className="text-xs font-sans text-ink/50">{t("grid.perNight")}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};
